import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';
import {
  PATH_CHANGE_PASSWORD,
  PATH_LOGIN,
} from '../../../routes/routes.constants';

/** Código que devuelve la API (403) mientras la contraseña sea temporal. */
const PASSWORD_CHANGE_REQUIRED = 'PASSWORD_CHANGE_REQUIRED';

/** Endpoints que crean/cierran la sesión: un 401 ahí es la respuesta final, no se reintenta. */
const SESSION_ENDPOINTS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/sso/'];

const withBearer = (req: HttpRequest<unknown>, token: string | null) =>
  token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

/**
 * El JWT solo se manda a nuestra API. Todo lo demás (SSO, PUT directo a R2) queda afuera.
 *
 * Importante para R2: las URLs prefirmadas ya llevan la firma en el query string, y mandar
 * además un header `Authorization` obliga al navegador a hacer preflight con un header que
 * la política CORS del bucket no permite → el request falla con un error de red opaco.
 */
const isOwnApi = (url: string): boolean => {
  try {
    const apiOrigin = new URL(environment.apiUrl, window.location.origin).origin;
    return new URL(url, window.location.origin).origin === apiOrigin;
  } catch {
    return false;
  }
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!isOwnApi(req.url)) {
    return next(req);
  }

  // Los endpoints de sesión no participan del refresh (evita recursión)
  const isAuthEndpoint = SESSION_ENDPOINTS.some((path) => req.url.includes(path));

  return next(withBearer(req, authService.getToken())).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 403 &&
        error.error?.code === PASSWORD_CHANGE_REQUIRED
      ) {
        router.navigate([`/${PATH_CHANGE_PASSWORD}`]);
        return throwError(() => error);
      }

      if (error.status !== 401 || isAuthEndpoint) {
        return throwError(() => error);
      }

      // 401 con sesión → refresh single-flight y un solo reintento
      return authService.refreshAccessToken().pipe(
        switchMap((token) => next(withBearer(req, token))),
        catchError(() => {
          authService.clearLocalSession();
          router.navigate([`/${PATH_LOGIN}`], {
            queryParams: { returnUrl: router.url },
          });
          return throwError(() => error);
        }),
      );
    }),
  );
};
